import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';

// Screen Components
import { LoadingScreen } from './components/screens/LoadingScreen';
import { SplashScreen } from './components/screens/SplashScreen';
import { StoryScreen } from './components/screens/StoryScreen';
import { MenuScreen } from './components/screens/MenuScreen';
import { GameScreen } from './modules/gameplay'; // updated import to use gameplay module barrel
import { InstructionsScreen } from './components/screens/InstructionsScreen';
import { CreditsScreen } from './components/screens/CreditsScreen';
import { SecretEndingScreen } from './components/screens/SecretEndingScreen';
import { GameOverScreen } from './components/screens/GameOverScreen';
import { EndingScreen } from './components/screens/EndingScreen';

// UI Components
import { MemoryFragmentPopup } from './components/ui/MemoryFragmentPopup';
import { MoralChoiceModal } from './components/ui/MoralChoiceModal';
import { EnvironmentalMessage } from './components/ui/EnvironmentalMessage';
import { ResetConfirmationModal } from './components/ui/ResetConfirmationModal';
import { SaveConfirmationModal } from './components/ui/SaveConfirmationModal';
import { VirusPuzzleModal } from './components/ui/VirusPuzzleModal';

// Controller Hook
import { useGameController } from './controllers/useGameController';
import { CONSTANTS } from './data';
import { GameState } from './systems/GameState';

function App() {
  const controller = useGameController();

  const {
    // core state
    gameState,
    setGameState,

    // discoveries and fusions
    discoveredCombinations,
    setDiscoveredCombinations,
    fusedElements,
    setFusedElements,

    // achievements
    unlockedAchievements,
    setUnlockedAchievements,
    achievementPopup,
    setAchievementPopup,

    // story/chapters/endings
    currentChapter,
    setCurrentChapter,
    activeEnding,

    // moral choices
    currentMoralEvent,
    handleMoralChoice,

    // memory fragments
    currentMemoryFragment,

    // chat
    showChatModal,
    setShowChatModal,
    chatMessages,
    setChatMessages,
    chatInput,
    setChatInput,
    isAiTyping,
    setIsAiTyping,
    handleTestAI,
    handleSendMessage,
    handleChatKeyPress,

    // AI interactions
    handleAIInteraction,

    // drag/drop
    handleDragStart,
    handleDragEnd,
    handleDragOver,
    handleCircleDrop,
    handleElementDrop,

    // menus and endings
    startNewGame,
    continueGame,
    handleStartEnding,
    handleRequestReset,
    confirmReset,
    cancelReset,
    showResetConfirm,
    requestMainMenu,
    saveAndExit,
    exitWithoutSaving,
    confirmExitWithoutSaving,
    cancelExit,
    showSaveConfirm,
    showExitWarning,

    // environmental horror
    environmentalMessage,
    setEnvironmentalMessage,
    screenFlash,

    // virus
    corruptedElements,
    startVirusPuzzle,
    showVirusPuzzle,
    closeVirusPuzzle,
    virusPuzzleGrid,
    handleGridClick,
    submitVirusPuzzle,
    virusPuzzleTimer,
    solvedVirusPatterns,
    virusPuzzleHintCells,
  } = controller;

  const gameStateManager = GameState.getInstance();

  const renderContent = () => {
    switch (gameState) {
      case 'loading':
        return <LoadingScreen onComplete={() => setGameState('splash')} />;
      case 'splash':
        return <SplashScreen onComplete={() => setGameState('story')} />;
      case 'story':
        return <StoryScreen onComplete={() => setGameState('menu')} />;
      case 'menu':
        return (
          <MenuScreen
            onStartNewGame={startNewGame}
            onContinueGame={continueGame}
            onShowInstructions={() => {
              controller.sounds.buttonClick();
              setGameState('instructions');
            }}
            onShowCredits={() => {
              controller.sounds.buttonClick();
              setGameState('credits');
            }}
            hasSaveData={gameStateManager.hasSaveData()}
          />
        );
      case 'playing':
        return (
          <GameScreen
            discoveredCombinations={discoveredCombinations}
            setDiscoveredCombinations={setDiscoveredCombinations}
            fusedElements={fusedElements}
            setFusedElements={setFusedElements}
            unlockedAchievements={unlockedAchievements}
            setUnlockedAchievements={setUnlockedAchievements}
            achievementPopup={achievementPopup}
            setAchievementPopup={setAchievementPopup}
            currentChapter={currentChapter}
            setCurrentChapter={setCurrentChapter}
            showChatModal={showChatModal}
            setShowChatModal={setShowChatModal}
            chatMessages={chatMessages}
            setChatMessages={setChatMessages}
            chatInput={chatInput}
            setChatInput={setChatInput}
            isAiTyping={isAiTyping}
            setIsAiTyping={setIsAiTyping}
            onMenuClick={requestMainMenu}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            onDragOver={handleDragOver}
            onCircleDrop={handleCircleDrop}
            onElementDrop={handleElementDrop}
            onTestAI={handleTestAI}
            onSendMessage={handleSendMessage}
            onChatKeyPress={handleChatKeyPress}
            onAIInteraction={handleAIInteraction}
            setGameState={setGameState}
            onStartEnding={handleStartEnding}
            onRequestReset={handleRequestReset}
            corruptedElements={corruptedElements}
            onStartVirusPuzzle={startVirusPuzzle}
          />
        );
      case 'instructions':
        return <InstructionsScreen onBack={() => setGameState('menu')} />;
      case 'credits':
        return <CreditsScreen onBack={() => setGameState('menu')} />;
      case 'secret_ending':
        return <SecretEndingScreen onBack={() => setGameState('menu')} />;
      case 'game_over':
        return <GameOverScreen onBack={() => setGameState('menu')} />;
      case 'ending': {
        const endingData = activeEnding ? (CONSTANTS.ENDING_PATHS as Record<string, any>)[activeEnding] : null;
        if (!endingData) {
          return <MenuScreen
            onStartNewGame={startNewGame}
            onContinueGame={continueGame}
            onShowInstructions={() => setGameState('instructions')}
            onShowCredits={() => setGameState('credits')}
            hasSaveData={gameStateManager.hasSaveData()}
          />;
        }
        return <EndingScreen 
          ending={endingData}
          onBack={() => setGameState('menu')} 
        />;
      }
      default:
        return <MenuScreen
          onStartNewGame={startNewGame}
          onContinueGame={continueGame}
          onShowInstructions={() => setGameState('instructions')}
          onShowCredits={() => setGameState('credits')}
          hasSaveData={gameStateManager.hasSaveData()}
        />;
    }
  };

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={gameState}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.5 }}
      >
        {renderContent()}
        
        <EnvironmentalMessage
          message={environmentalMessage}
          onComplete={() => setEnvironmentalMessage(null)}
        />

        {screenFlash && (
          <div className={`screen-flash-overlay ${screenFlash}`}></div>
        )}

        <MoralChoiceModal 
          moralEvent={currentMoralEvent}
          onChoice={controller.handleMoralChoice}
        />

        <MemoryFragmentPopup
          memoryFragment={currentMemoryFragment}
          onClose={() => {
            controller.sounds.buttonClick();
            controller.setCurrentMemoryFragment(null);
          }}
        />

        <ResetConfirmationModal
          isOpen={showResetConfirm}
          onConfirm={confirmReset}
          onCancel={cancelReset}
        />

        <SaveConfirmationModal
          isOpen={showSaveConfirm}
          isWarningVisible={showExitWarning}
          onSaveAndExit={saveAndExit}
          onExitWithoutSaving={exitWithoutSaving}
          onConfirmExitWithoutSaving={confirmExitWithoutSaving}
          onCancel={cancelExit}
        />

        <VirusPuzzleModal
          isOpen={showVirusPuzzle}
          onClose={closeVirusPuzzle}
          grid={virusPuzzleGrid}
          onCellClick={handleGridClick}
          onSubmit={submitVirusPuzzle}
          timeLeft={virusPuzzleTimer}
          patterns={CONSTANTS.VIRUS_PUZZLE.patterns}
          solvedPatterns={solvedVirusPatterns}
          hintCells={virusPuzzleHintCells}
        />
      </motion.div>
    </AnimatePresence>
  );
}

export default App;
